import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { DisplayService } from '../../api/gen/api/display.service';
import { ParkingSpaceService } from '../../api/gen/api/parkingSpace.service';
import { DeviceGroupService } from '../../api/gen/api/deviceGroup.service';
import { DisplayWithHealthDto, ParkingSpaceDto } from '../../api/gen';
import { DisplayState } from '../../api/gen/model/displayState';
import { suggestedLiveDataKeys } from './models/display-live-data-keys';
import { Role } from 'shared-lib';
import { RoleService } from '../auth/role-service';
import { DisplayTemplateEditorComponent } from './display-template-editor/display-template-editor.component';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-display-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSelectModule,
    MatDividerModule,
    DisplayTemplateEditorComponent
  ],
  templateUrl: './display-management.component.html',
  styleUrl: './display-management.component.scss'
})
export class DisplayManagementComponent implements OnInit {

  private readonly displayService = inject(DisplayService);
  private readonly parkingSpaceService = inject(ParkingSpaceService);
  private readonly deviceGroupService = inject(DeviceGroupService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly roleService = inject(RoleService);

  // Sites
  sites = signal<ParkingSpaceDto[]>([]);
  selectedSite = signal<ParkingSpaceDto | null>(null);
  loadingSites = signal(false);
  siteSearch = '';
  isLoadingSites = signal(false);

  // Displays
  displays = signal<DisplayWithHealthDto[]>([]);
  loadingDisplays = signal(false);
  gateNames = signal<Record<string, string>>({});
  actionInProgress = signal<Set<string>>(new Set());

  // Template editor
  templateDisplay = signal<DisplayWithHealthDto | null>(null);

  // Quick action forms
  customMessageDisplayId = signal<string | null>(null);
  customMessageText = '';
  customMessageTtl: number | null = null;
  pushStateDisplayId = signal<string | null>(null);
  pushStateValue: DisplayState | null = null;
  pushStateLiveData = signal<{ key: string; value: string }[]>([]);

  isAdmin = signal(false);
  readonly DisplayState = DisplayState;
  readonly allDisplayStates = Object.values(DisplayState);
  readonly displayedColumns = ['name', 'gate', 'state', 'override', 'heartbeat', 'actions'];

  async ngOnInit(): Promise<void> {
    this.isAdmin.set(await this.roleService.checkAccess(Role.Administrator));
    this.loadSites();
  }

  get filteredSites(): ParkingSpaceDto[] {
    const q = this.siteSearch.toLowerCase().trim();
    if (!q) return this.sites();
    return this.sites().filter(s => s.name?.toLowerCase().includes(q));
  }

  loadSites(): void {
    this.isLoadingSites.set(true);
    this.loadingSites.set(true);
    this.parkingSpaceService.parkingSpaceFilterPost(
      undefined, undefined, undefined, undefined, undefined, undefined, 0, 100
    ).subscribe({
      next: result => {
        this.sites.set(result?.items ?? []);
        this.loadingSites.set(false);
        this.isLoadingSites.set(false);
      },
      error: () => {
        this.snackBar.open('Failed to load parking sites', 'Close', { duration: 3000 });
        this.loadingSites.set(false);
        this.isLoadingSites.set(false);
      }
    });
  }

  selectSite(site: ParkingSpaceDto): void {
    if (this.selectedSite()?.id === site.id) return;
    this.selectedSite.set(site);
    this.templateDisplay.set(null);
    this.customMessageDisplayId.set(null);
    this.pushStateDisplayId.set(null);
    this.loadDisplays(site.id!);
  }

  loadDisplays(siteId: string): void {
    this.loadingDisplays.set(true);
    this.displays.set([]);
    this.displayService.displayGet(siteId, undefined, undefined, undefined, undefined, 200)
      .subscribe({
        next: result => {
          const items = result?.items ?? [];
          this.displays.set(items);
          this.resolveGateNames(items);
          this.loadingDisplays.set(false);
        },
        error: () => {
          this.snackBar.open('Failed to load displays', 'Close', { duration: 3000 });
          this.loadingDisplays.set(false);
        }
      });
  }

  private async resolveGateNames(displays: DisplayWithHealthDto[]): Promise<void> {
    const uniqueIds = [
      ...new Set(displays.map(d => d.gateId).filter((id): id is string => !!id))
    ];
    if (uniqueIds.length === 0) return;
    const current = { ...this.gateNames() };
    const toFetch = uniqueIds.filter(id => !current[id]);
    if (toFetch.length === 0) return;
    try {
      const groups = await firstValueFrom(
        this.deviceGroupService.deviceGroupsByIdsPost({ deviceGroupIds: toFetch })
      );
      for (const g of groups) {
        if (g.id) current[g.id] = g.groupName ?? g.id;
      }
      this.gateNames.set(current);
    } catch {
      for (const id of toFetch) current[id] = id;
      this.gateNames.set(current);
    }
  }

  getGateName(gateId: string | null | undefined): string {
    if (!gateId) return '—';
    return this.gateNames()[gateId] ?? gateId;
  }

  isActionBusy(displayId: string | undefined): boolean {
    if (!displayId) return false;
    return this.actionInProgress().has(displayId);
  }

  private setBusy(displayId: string, busy: boolean): void {
    this.actionInProgress.update(set => {
      const next = new Set(set);
      busy ? next.add(displayId) : next.delete(displayId);
      return next;
    });
  }

  async onEmergency(display: DisplayWithHealthDto): Promise<void> {
    const id = display.displayId!;
    const isActive = display.currentState === DisplayState.Emergency;
    this.setBusy(id, true);
    try {
      if (isActive) {
        await firstValueFrom(this.displayService.displayDisplayIdEmergencyDelete(id));
        this.snackBar.open('Emergency cleared', 'Close', { duration: 2000 });
      } else {
        await firstValueFrom(this.displayService.displayDisplayIdEmergencyPost(id));
        this.snackBar.open('Emergency activated', 'Close', { duration: 2000 });
      }
      this.loadDisplays(this.selectedSite()!.id!);
    } catch {
      this.snackBar.open('Emergency action failed', 'Close', { duration: 3000 });
    } finally {
      this.setBusy(id, false);
    }
  }

  async onMaintenance(display: DisplayWithHealthDto): Promise<void> {
    const id = display.displayId!;
    const isActive = display.currentState === DisplayState.Maintenance;
    this.setBusy(id, true);
    try {
      if (isActive) {
        await firstValueFrom(this.displayService.displayDisplayIdMaintenanceDelete(id));
        this.snackBar.open('Maintenance cleared', 'Close', { duration: 2000 });
      } else {
        await firstValueFrom(this.displayService.displayDisplayIdMaintenancePost(id));
        this.snackBar.open('Maintenance activated', 'Close', { duration: 2000 });
      }
      this.loadDisplays(this.selectedSite()!.id!);
    } catch {
      this.snackBar.open('Maintenance action failed', 'Close', { duration: 3000 });
    } finally {
      this.setBusy(id, false);
    }
  }

  openCustomMessage(displayId: string): void {
    this.customMessageDisplayId.set(displayId);
    this.customMessageText = '';
    this.customMessageTtl = null;
    this.pushStateDisplayId.set(null);
  }

  closeCustomMessage(): void {
    this.customMessageDisplayId.set(null);
  }

  async sendCustomMessage(displayId: string): Promise<void> {
    if (!this.customMessageText.trim()) return;
    this.setBusy(displayId, true);
    try {
      await firstValueFrom(this.displayService.displayDisplayIdCustomMessagePost(displayId, {
        message: this.customMessageText,
        ttlSeconds: this.customMessageTtl ?? undefined
      }));
      this.snackBar.open('Custom message sent', 'Close', { duration: 2000 });
      this.customMessageDisplayId.set(null);
    } catch {
      this.snackBar.open('Failed to send custom message', 'Close', { duration: 3000 });
    } finally {
      this.setBusy(displayId, false);
    }
  }

  async clearCustomMessage(displayId: string): Promise<void> {
    this.setBusy(displayId, true);
    try {
      await firstValueFrom(this.displayService.displayDisplayIdCustomMessageDelete(displayId));
      this.snackBar.open('Custom message cleared', 'Close', { duration: 2000 });
    } catch {
      this.snackBar.open('Failed to clear custom message', 'Close', { duration: 3000 });
    } finally {
      this.setBusy(displayId, false);
    }
  }

  openPushState(displayId: string): void {
    this.pushStateDisplayId.set(displayId);
    this.pushStateValue = null;
    // Seed common entry-flow token keys so testing a state (e.g. DriveAuthorized) is one step.
    this.pushStateLiveData.set([{ key: 'plate', value: '' }, { key: 'msg', value: '' }]);
    this.customMessageDisplayId.set(null);
  }

  closePushState(): void {
    this.pushStateDisplayId.set(null);
  }

  addLiveDataRow(): void {
    this.pushStateLiveData.update(rows => [...rows, { key: '', value: '' }]);
  }

  /**
   * Keys the selected state actually arrives with, offered as one-tap chips.
   *
   * Without this the key field is a blank box with no way to learn the vocabulary, and a key that
   * does not match is accepted silently and then ignored by the layer looking for a different name —
   * which reads on the display as "the widget is broken".
   */
  suggestedKeys(): string[] {
    const used = new Set(this.pushStateLiveData().map(r => r.key.trim()));
    return suggestedLiveDataKeys(this.pushStateValue).filter(k => !used.has(k));
  }

  /** Add a suggested key, reusing the first blank row rather than stacking an empty one above it. */
  addSuggestedKey(key: string): void {
    this.pushStateLiveData.update(rows => {
      const blank = rows.findIndex(r => !r.key.trim() && !r.value.trim());
      if (blank >= 0) {
        const next = [...rows];
        next[blank] = { key, value: '' };
        return next;
      }
      return [...rows, { key, value: '' }];
    });
  }

  removeLiveDataRow(index: number): void {
    this.pushStateLiveData.update(rows => rows.filter((_, i) => i !== index));
  }

  updateLiveDataKey(index: number, key: string): void {
    this.pushStateLiveData.update(rows => rows.map((row, i) => i === index ? { ...row, key } : row));
  }

  updateLiveDataValue(index: number, value: string): void {
    this.pushStateLiveData.update(rows => rows.map((row, i) => i === index ? { ...row, value } : row));
  }

  /** Builds the LiveData token map from the entered rows, skipping blank keys. Undefined when empty. */
  private buildLiveData(): Record<string, string> | undefined {
    const entries = this.pushStateLiveData().filter(row => row.key.trim());
    if (entries.length === 0) return undefined;
    return entries.reduce<Record<string, string>>((map, row) => {
      map[row.key.trim()] = row.value;
      return map;
    }, {});
  }

  async sendPushState(displayId: string): Promise<void> {
    if (!this.pushStateValue) return;
    this.setBusy(displayId, true);
    try {
      await firstValueFrom(this.displayService.displayDisplayIdStatePost(displayId, {
        state: this.pushStateValue,
        liveData: this.buildLiveData()
      }));
      this.snackBar.open('State pushed', 'Close', { duration: 2000 });
      this.pushStateDisplayId.set(null);
      this.loadDisplays(this.selectedSite()!.id!);
    } catch {
      this.snackBar.open('Failed to push state', 'Close', { duration: 3000 });
    } finally {
      this.setBusy(displayId, false);
    }
  }

  async onHeartbeat(displayId: string): Promise<void> {
    this.setBusy(displayId, true);
    try {
      await firstValueFrom(this.displayService.displaysDisplayIdHeartbeatPost(displayId));
      this.snackBar.open('Heartbeat sent', 'Close', { duration: 2000 });
      this.loadDisplays(this.selectedSite()!.id!);
    } catch {
      this.snackBar.open('Heartbeat failed', 'Close', { duration: 3000 });
    } finally {
      this.setBusy(displayId, false);
    }
  }

  openTemplateEditor(display: DisplayWithHealthDto): void {
    this.templateDisplay.set(display);
    this.customMessageDisplayId.set(null);
    this.pushStateDisplayId.set(null);
  }

  closeTemplateEditor(): void {
    this.templateDisplay.set(null);
  }

  getStateColor(state: DisplayState | undefined): string {
    if (!state) return 'none';
    const map: Partial<Record<DisplayState, string>> = {
      [DisplayState.Emergency]: 'emergency',
      [DisplayState.Maintenance]: 'maintenance',
      [DisplayState.CustomMessage]: 'custom',
      [DisplayState.Idle]: 'idle',
      [DisplayState.Full]: 'full',
    };
    return map[state] ?? 'default';
  }
  deselectSite() {
    this.selectedSite.set(null);
    this.templateDisplay.set(null);
    this.customMessageDisplayId.set(null);
    this.pushStateDisplayId.set(null);
  }

  /** Deep link to this display's live view in the edge-app, or null when ids are missing. */
  getEdgeUrl(displayId: string | null | undefined): string | null {
    const siteId = this.selectedSite()?.id;
    if (!siteId || !displayId) return null;
    return `${environment.edgeUrl}/parking-sites/${siteId}/display/${displayId}`;
  }
}
